/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import { authApi, villaApi } from "./api";
import { EyeIcon, EyeOffIcon } from "../icons/EyeIcons";
import "./LoginModal.css";
import "./RegisterModal.css";

const STEPS = [
  { number: 1, label: "Basic Info" },
  { number: 2, label: "Phone Verify" },
  { number: 3, label: "Villa" },
  { number: 4, label: "Confirm" },
];

const ROLE_MAP = {
  primary: "PRIMARY_OWNER",
  resident: "RESIDENT",
  tenant: "TENANT",
};

const ROLE_LABEL = {
  PRIMARY_OWNER: "Primary Owner",
  RESIDENT: "Resident",
  TENANT: "Tenant",
};

function ProgressIndicator({ currentStep }) {
  const progress = (currentStep / STEPS.length) * 100;
  return (
    <div className="vs-progress">
      <div className="vs-progress-steps">
        {STEPS.map((s, i) => {
          const completed = currentStep > s.number;
          const active = currentStep === s.number;
          return (
            <div key={s.number} className="vs-progress-step">
              <div className={`vs-progress-circle ${completed ? "completed" : active ? "active" : ""}`}>
                {completed ? "✓" : s.number}
              </div>
              <span className={`vs-progress-label ${active ? "active" : completed ? "completed" : ""}`}>
                {s.label}
              </span>
              {i < STEPS.length - 1 && (
                <div className={`vs-progress-line ${completed ? "completed" : ""}`} />
              )}
            </div>
          );
        })}
      </div>
      <div className="vs-progress-bar">
        <div className="vs-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <p className="vs-progress-text">
        Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].label} ({Math.round(progress)}%)
      </p>
    </div>
  );
}

const PASSWORD_REQS = [
  { label: "Min 8 characters", test: (p) => p.length >= 8 },
  { label: "Uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "Number", test: (p) => /[0-9]/.test(p) },
  { label: "Special character", test: (p) => /[!@#$%^&*]/.test(p) },
];

export default function RegisterModal({ open, onClose, onSwitchToLogin, onAuthenticated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreed: false,
    villaId: "",
    villaNumber: "",
    role: "primary",
    relation: "",
    tenantEndDate: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(60);
  const [token, setToken] = useState("");
  const [user, setUser] = useState(null);
  const [villas, setVillas] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setForm({
        fullName: "",
        phone: "",
        email: "",
        password: "",
        confirmPassword: "",
        agreed: false,
        villaId: "",
        villaNumber: "",
        role: "primary",
        relation: "",
        tenantEndDate: "",
      });
      setOtp(["", "", "", "", "", ""]);
      setToken("");
      setUser(null);
      setVillas([]);
      setSearch("");
      setError("");
      setPhoneError("");
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (step !== 2 || resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [step, resendTimer]);

  useEffect(() => {
    if (step === 3 && token && villas.length === 0) {
      villaApi.list(token).then(setVillas).catch((e) => setError(e.message));
    }
  }, [step, token, villas.length]);

  if (!open) return null;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submitBasic = async (e) => {
    e.preventDefault();
    setError("");
    if (form.phone.length !== 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await authApi.register({
        phone_number: form.phone,
        name: form.fullName,
        email: form.email || null,
        password: form.password,
        user_type: form.role === "primary" ? "PRIMARY_USER" : "SECONDARY_USER",
      });
      setStep(2);
      setResendTimer(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpChange = (i, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[i] = value.slice(-1);
    setOtp(next);
    if (value && i < 5) otpRefs.current[i + 1]?.focus();
  };

  const handleOtpKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData("text") || "").slice(0, 6);
    if (!/^\d+$/.test(text)) return;
    const next = [...otp];
    text.split("").forEach((c, i) => {
      if (i < 6) next[i] = c;
    });
    setOtp(next);
    otpRefs.current[Math.min(text.length, 5)]?.focus();
  };

  const verifyOtp = async () => {
    setError("");
    const code = otp.join("");
    if (code.length !== 6) return;
    setSubmitting(true);
    try {
      const res = await authApi.verifyOtp({ phone_number: form.phone, otp_code: code });
      // Persist tokens so the next step (villa request) is authenticated,
      // but DO NOT call onAuthenticated yet — that flips App.js to the
      // logged-in shell and would unmount this modal before the user
      // gets a chance to pick a villa.
      setToken(res.access_token);
      setUser(res.user);
      localStorage.setItem("access_token", res.access_token);
      localStorage.setItem("refresh_token", res.refresh_token);
      localStorage.setItem("user", JSON.stringify(res.user));
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = () => {
    // End of step 4 — now propagate authentication to App.js so the
    // shell takes over.
    if (user) {
      onAuthenticated && onAuthenticated(user);
    }
    onClose();
  };

  const handleExit = () => {
    // The user closed the modal mid-flow. If they have already verified
    // their phone (step ≥ 3), they're authenticated against the backend
    // — surface that to App.js so they land in the shell instead of
    // being silently stranded as "logged out" with valid tokens in
    // localStorage.
    if (step >= 3 && user) {
      onAuthenticated && onAuthenticated(user);
    }
    onClose();
  };

  const submitVilla = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.villaId) {
      setError("Please select a villa");
      return;
    }
    setSubmitting(true);
    try {
      await villaApi.requestAccess(
        {
          villa_id: form.villaId,
          role: ROLE_MAP[form.role],
          relation: form.relation || null,
          tenant_end_date: form.role === "tenant" && form.tenantEndDate ? form.tenantEndDate : null,
        },
        token
      );
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredVillas = search
    ? villas.filter((v) =>
        v.villa_number.toLowerCase().includes(search.toLowerCase())
      )
    : villas;

  return (
    <div className="vs-login-overlay" onClick={handleExit}>
      <div className="vs-login-modal vs-register-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vs-login-header">
          <h2>Create Your Account</h2>
          <button className="vs-login-close" onClick={handleExit} aria-label="Close">×</button>
        </div>

        <ProgressIndicator currentStep={step} />

        {step === 1 && (
          <form onSubmit={submitBasic} className="vs-login-form vs-register-step">
            <h3 className="vs-step-title">Basic Information</h3>

            <div className="vs-field">
              <label htmlFor="vs-fullName">Full Name <span className="vs-required">*</span></label>
              <input
                id="vs-fullName"
                type="text"
                required
                value={form.fullName}
                onChange={(e) => update({ fullName: e.target.value })}
                placeholder="Enter your full name"
                className="vs-input"
              />
            </div>

            <div className="vs-field">
              <label htmlFor="vs-r-phone">Phone Number <span className="vs-required">*</span></label>
              <div className="vs-phone-row">
                <span className="vs-phone-prefix">+91</span>
                <input
                  id="vs-r-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  required
                  value={form.phone}
                  onChange={(e) => {
                    update({ phone: e.target.value.replace(/\D/g, "") });
                    setPhoneError("");
                  }}
                  onBlur={async () => {
                    if (form.phone.length !== 10) return;
                    try {
                      const res = await authApi.checkPhone(form.phone);
                      if (!res.available) {
                        setPhoneError(
                          "This number is already registered — log in or use a different number."
                        );
                      }
                    } catch {}
                  }}
                  placeholder="10-digit mobile number"
                />
              </div>
              {phoneError ? (
                <p className="vs-hint vs-hint-error">
                  {phoneError}{" "}
                  <button type="button" className="vs-link" onClick={onSwitchToLogin}>
                    Log in
                  </button>
                </p>
              ) : (
                <p className="vs-hint">We'll verify via WhatsApp</p>
              )}
            </div>

            <div className="vs-field">
              <label htmlFor="vs-email">Email (optional)</label>
              <input
                id="vs-email"
                type="email"
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
                placeholder="your@email.com"
                className="vs-input"
              />
            </div>

            <div className="vs-field">
              <label htmlFor="vs-r-password">Password <span className="vs-required">*</span></label>
              <div className="vs-password-row">
                <input
                  id="vs-r-password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={form.password}
                  onChange={(e) => update({ password: e.target.value })}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  className="vs-password-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                </button>
              </div>
              <div className="vs-password-reqs">
                {PASSWORD_REQS.map((req, i) => {
                  const ok = req.test(form.password);
                  return (
                    <div key={i} className={`vs-req ${ok ? "ok" : ""}`}>
                      <span>{ok ? "✓" : "○"}</span>
                      <span>{req.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="vs-field">
              <label htmlFor="vs-confirmPassword">Confirm Password <span className="vs-required">*</span></label>
              <div className="vs-password-row">
                <input
                  id="vs-confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  required
                  value={form.confirmPassword}
                  onChange={(e) => update({ confirmPassword: e.target.value })}
                  placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  className="vs-password-toggle"
                  onClick={() => setShowConfirm((s) => !s)}
                >
                  {showConfirm ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                </button>
              </div>
              {form.confirmPassword && form.password !== form.confirmPassword && (
                <p className="vs-hint vs-hint-error">Passwords do not match</p>
              )}
            </div>

            <label className="vs-terms">
              <input
                type="checkbox"
                checked={form.agreed}
                onChange={(e) => update({ agreed: e.target.checked })}
              />
              <span>
                I agree to the <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Use</a> and{" "}
                <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </span>
            </label>

            {error && <div className="vs-error">{error}</div>}

            <button
              type="submit"
              className="vs-btn-primary"
              disabled={
                submitting ||
                !form.agreed ||
                !form.fullName ||
                form.password !== form.confirmPassword ||
                form.phone.length !== 10 ||
                !!phoneError
              }
            >
              {submitting ? "Sending OTP…" : "Next"}
            </button>

            <div className="vs-switch-auth">
              Already have an account?{" "}
              <button
                type="button"
                className="vs-link"
                onClick={() => {
                  onClose();
                  onSwitchToLogin && onSwitchToLogin();
                }}
              >
                Login
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="vs-otp-wrap vs-register-step">
            <h3 className="vs-step-title">Verify Your Phone Number</h3>
            <p className="vs-otp-subtitle">We've sent a WhatsApp OTP to verify your number</p>

            <div className="vs-otp-phone">
              <div className="vs-otp-phone-label">Phone Number</div>
              <div className="vs-otp-phone-value">+91 {form.phone}</div>
              <button type="button" className="vs-link" onClick={() => setStep(1)}>
                Change Number
              </button>
            </div>

            <div className="vs-field">
              <label>Enter 6-digit OTP</label>
              <div className="vs-otp-inputs">
                {otp.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKey(i, e)}
                    onPaste={handleOtpPaste}
                  />
                ))}
              </div>
            </div>

            <div className="vs-otp-resend">
              <p>Didn't receive OTP?</p>
              {resendTimer > 0 ? (
                <p className="vs-muted">Resend OTP in {resendTimer}s</p>
              ) : (
                <button
                  type="button"
                  className="vs-link"
                  onClick={() => {
                    setOtp(["", "", "", "", "", ""]);
                    setResendTimer(60);
                  }}
                >
                  Resend OTP
                </button>
              )}
            </div>

            {error && <div className="vs-error">{error}</div>}

            <div className="vs-otp-actions">
              <button type="button" className="vs-btn-secondary" onClick={() => setStep(1)}>
                Back
              </button>
              <button
                type="button"
                className="vs-btn-primary"
                onClick={verifyOtp}
                disabled={otp.join("").length !== 6 || submitting}
              >
                {submitting ? "Verifying…" : "Verify"}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <form onSubmit={submitVilla} className="vs-register-step">
            <h3 className="vs-step-title">Villa Access Request</h3>
            <p className="vs-otp-subtitle">Which villa are you requesting access for?</p>

            <div className="vs-field">
              <label>Search Villa <span className="vs-required">*</span></label>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (!e.target.value) update({ villaId: "", villaNumber: "" });
                }}
                placeholder="Search by villa number…"
                className="vs-input"
              />
              {!form.villaId && villas.length > 0 && (
                <div className="vs-villa-list">
                  {filteredVillas.length === 0 && <div className="vs-villa-empty">No villas found</div>}
                  {filteredVillas.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className="vs-villa-item"
                      onClick={() => {
                        update({ villaId: v.id, villaNumber: v.villa_number });
                        setSearch(v.villa_number);
                      }}
                    >
                      <div className="vs-villa-name">{v.villa_number}</div>
                      <div className="vs-villa-meta">{v.address}</div>
                    </button>
                  ))}
                </div>
              )}
              {form.villaId && (
                <div className="vs-villa-selected">Selected: {form.villaNumber}</div>
              )}
            </div>

            <div className="vs-field">
              <label>Select Your Role <span className="vs-required">*</span></label>
              <div className="vs-role-list">
                {[
                  { value: "primary", title: "Primary Owner", subtitle: "I am the villa owner", info: "Will be reviewed by admin" },
                  { value: "resident", title: "Resident", subtitle: "Family member or household resident", info: "Will be reviewed by villa owner" },
                  { value: "tenant", title: "Tenant", subtitle: "Temporary resident", info: "Will be reviewed by villa owner" },
                ].map((r) => (
                  <label key={r.value} className={`vs-role ${form.role === r.value ? "active" : ""}`}>
                    <input
                      type="radio"
                      name="role"
                      value={r.value}
                      checked={form.role === r.value}
                      onChange={() => update({ role: r.value })}
                    />
                    <div className="vs-role-body">
                      <div className="vs-role-title">{r.title}</div>
                      <div className="vs-role-subtitle">{r.subtitle}</div>
                      <div className="vs-role-info">ⓘ {r.info}</div>
                      {r.value === "tenant" && form.role === "tenant" && (
                        <div className="vs-field" style={{ marginTop: 10 }}>
                          <label>Access until</label>
                          <input
                            type="date"
                            value={form.tenantEndDate}
                            onChange={(e) => update({ tenantEndDate: e.target.value })}
                            className="vs-input"
                          />
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {form.role === "resident" && (
              <div className="vs-field">
                <label>Relation/Notes (optional)</label>
                <textarea
                  value={form.relation}
                  onChange={(e) => update({ relation: e.target.value })}
                  rows={3}
                  placeholder="e.g., Daughter, Son, Spouse…"
                  className="vs-input"
                />
              </div>
            )}

            {error && <div className="vs-error">{error}</div>}

            <div className="vs-otp-actions">
              <button type="button" className="vs-btn-secondary" onClick={() => setStep(2)}>
                Back
              </button>
              <button type="submit" className="vs-btn-primary" disabled={!form.villaId || submitting}>
                {submitting ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </form>
        )}

        {step === 4 && (
          <div className="vs-register-step">
            <div className="vs-success">
              <div className="vs-success-circle">✓</div>
              <h3 className="vs-success-title">Registration Successful!</h3>
              <p className="vs-success-subtitle">Your Villa Access Request Has Been Sent</p>
            </div>

            <div className="vs-summary">
              <div className="vs-summary-row"><span>Name:</span><strong>{form.fullName}</strong></div>
              <div className="vs-summary-row"><span>Phone:</span><strong>+91 {form.phone}</strong></div>
              {form.email && (
                <div className="vs-summary-row"><span>Email:</span><strong>{form.email}</strong></div>
              )}
              <div className="vs-summary-row"><span>Villa:</span><strong>{form.villaNumber}</strong></div>
              <div className="vs-summary-row"><span>Role:</span><strong>{ROLE_LABEL[ROLE_MAP[form.role]]}</strong></div>
              <div className="vs-summary-status">
                <span>Status:</span>
                <span className="vs-status-badge">PENDING APPROVAL</span>
              </div>
            </div>

            <div className="vs-next-steps">
              <h4>What Happens Next?</h4>
              <ul>
                <li className="done">✓ Your details have been registered</li>
                <li>⏱ {form.role === "primary" ? "Admin" : "Primary owner"} will review (1-2 days)</li>
                <li>✉ You'll get a notification</li>
                <li>✓ Then you can access villa features</li>
              </ul>
            </div>

            <div className="vs-otp-actions">
              <button type="button" className="vs-btn-primary" onClick={handleFinish}>
                Continue to your account
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
